import React from "react"
import {
    Box,
    Button,
    ButtonVariant,
    componentStyles,
    HorizontalStack,
    Spinner,
    Text,
    useColors,
    useTheme,
    VerticalStack,
} from "@mallardbay/lib-react-components"

import SparkleIcon from "~components/shared/icons/sparkle-icon"
import COPY from "~config/copy-constants"
import AssistantTitle from "~components/outfitter-assistant/assistant-title"

interface Props {
    readonly suggestions: readonly string[]
    readonly isLoading: boolean
    readonly onSelect: (suggestion: string) => void
}

export default function AssistantQuickSuggestions({
    suggestions,
    isLoading,
    onSelect,
}: Props) {
    const styles = useStyles()
    const shouldShowSuggestions = !isLoading && suggestions.length > 0
    const shouldShowSuggestionsSection = isLoading || shouldShowSuggestions

    return (
        <VerticalStack style={styles.root}>
            <Box style={styles.heroIcon}>
                <SparkleIcon size={42} isBrandColor />
            </Box>
            <VerticalStack style={styles.intro}>
                <AssistantTitle
                    title={COPY.OUTFITTER_ASSISTANT_EMPTY_STATE_TITLE}
                    size="lg"
                />
                <Text style={styles.description}>
                    {COPY.OUTFITTER_ASSISTANT_EMPTY_STATE}
                </Text>
            </VerticalStack>
            {shouldShowSuggestionsSection && (
                <>
                    <Text style={styles.label}>
                        {COPY.OUTFITTER_ASSISTANT_QUICK_SUGGESTIONS_LABEL}
                    </Text>
                    {isLoading && (
                        <HorizontalStack style={styles.loading}>
                            <Spinner size="sm" shouldUseTopMargin={false} />
                        </HorizontalStack>
                    )}
                    {shouldShowSuggestions && (
                        <VerticalStack style={styles.suggestions}>
                            {suggestions.map((suggestion, index) => (
                                <Button
                                    key={`${suggestion}-${index}`}
                                    style={styles.suggestionButton}
                                    variant={ButtonVariant.Unstyled}
                                    leftIcon={
                                        <SparkleIcon size={16} isBrandColor />
                                    }
                                    onClick={() => onSelect(suggestion)}
                                >
                                    {suggestion}
                                </Button>
                            ))}
                        </VerticalStack>
                    )}
                </>
            )}
        </VerticalStack>
    )
}

function useStyles() {
    const colors = useColors()
    const theme = useTheme()

    return componentStyles({
        loading: {
            justifyContent: "center",
            marginTop: 3,
        },
        root: {
            alignItems: "stretch",
            gap: 4,
            paddingTop: 12,
            width: "100%",
        },
        heroIcon: {
            alignSelf: "center",
            color: colors.brandDefault,
            lineHeight: 1,
        },
        intro: {
            alignItems: "center",
            gap: 1,
            textAlign: "center",
        },
        description: {
            maxWidth: "320px",
            color: colors.secondaryText,
            lineHeight: 1.5,
        },
        label: {
            color: colors.secondaryText,
            fontWeight: theme.fontWeights.semibold,
            textAlign: "center",
            marginTop: 1,
        },
        suggestions: {
            alignItems: "stretch",
            gap: 3,
            width: "100%",
        },
        suggestionButton: {
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 3,
            width: "100%",
            height: "auto",
            minHeight: "68px",
            padding: 4,
            borderRadius: theme.radii.lg,
            backgroundColor: colors.bgSecondary,
            color: colors.text,
            fontWeight: theme.fontWeights.medium,
            lineHeight: 1.35,
            textAlign: "left",
            whiteSpace: "normal",
        },
    })
}
